import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface NewDiscussionModalProps {
  showNewDiscussionModal: boolean;
  setShowNewDiscussionModal: (show: boolean) => void;
  newDiscussionTitle: string;
  setNewDiscussionTitle: (title: string) => void;
  newDiscussionCategory: string;
  setNewDiscussionCategory: (category: string) => void;
  newDiscussionContent: string;
  setNewDiscussionContent: (content: string) => void;
  allCategories: Array<{id: string, name: string}>;
  handleCreateDiscussion: () => void;
  session: any;
  setShowLoginModal: (show: boolean) => void;
  createDiscussionMutation: { isPending: boolean };
}

const NewDiscussionModal: React.FC<NewDiscussionModalProps> = ({
  showNewDiscussionModal,
  setShowNewDiscussionModal,
  newDiscussionTitle,
  setNewDiscussionTitle,
  newDiscussionCategory,
  setNewDiscussionCategory,
  newDiscussionContent,
  setNewDiscussionContent,
  allCategories,
  handleCreateDiscussion,
  session,
  setShowLoginModal,
  createDiscussionMutation
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center border-b border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900">Start New Discussion</h2>
          <button 
            className="text-gray-400 hover:text-gray-500"
            onClick={() => setShowNewDiscussionModal(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="title">
              Title
            </label>
            <input 
              type="text" 
              id="title" 
              key="title"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Write a descriptive title for your discussion"
              value={newDiscussionTitle}
              onChange={(e) => setNewDiscussionTitle(e.target.value)}
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="category">
              Category
            </label>
            <select 
              id="category" 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={newDiscussionCategory}
              onChange={(e) => setNewDiscussionCategory(e.target.value)}
            >
              <option value="">Select a category</option>
              {allCategories.filter(cat => cat.id !== 'all').map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="content">
              Content
            </label>
            <textarea 
              id="content" 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 resize-none h-40"
              placeholder="Describe your question or share your thoughts in detail..."
              value={newDiscussionContent}
              onChange={(e) => setNewDiscussionContent(e.target.value)}
            ></textarea>
          </div>
          
          <div className="flex items-center mb-6">
            <input 
              type="checkbox" 
              id="notification" 
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700" htmlFor="notification">
              Notify me of responses to this discussion
            </label>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-600 mb-6 flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mr-2" />
            <p>
              Remember to check if your question has already been asked. Do not share specific test questions 
              from official LSAT exams or licensed prep materials.
            </p>
          </div>
          
          <div className="flex justify-end space-x-4">
            <button 
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              onClick={() => setShowNewDiscussionModal(false)}
            >
              Cancel
            </button>
            <button 
              className={`px-6 py-2 ${
                createDiscussionMutation.isPending || !newDiscussionTitle.trim() || !newDiscussionContent.trim() || !newDiscussionCategory
                  ? 'bg-indigo-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              } text-white rounded-md transition-colors`}
              onClick={!session ? () => setShowLoginModal(true) : handleCreateDiscussion}
              disabled={createDiscussionMutation.isPending || !newDiscussionTitle.trim() || !newDiscussionContent.trim() || !newDiscussionCategory}
            >
              {createDiscussionMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                  Posting...
                </>
              ) : (
                'Post Discussion'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewDiscussionModal;
